import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Avatar from 'flarum/common/components/Avatar';
import Api from '../utils/Api';
import LessonOptions from '../utils/LessonOptions';
// v0.18.0 (辉哥 10/18 拍板) — 课评 (学员端**只读**)
import { fetchReviewMap, openReviewModal, starsFor } from '../utils/LessonReviews';
// 批 41 · 段① — 无限滚动 + IntersectionObserver 整套机制现在共用 utils/InfiniteScroll.js
//   ⚠️ 本弹层的父类是 Flarum 的 **Modal** (不是 Component) ⇒ 不能直接 extends InfiniteScrollBase
//      (那样会丢掉 Modal 的标题栏 / 关闭按钮 / 焦点陷阱 / 遮罩 —— 弹层整个坏掉)
//      ⇒ 用 withInfiniteScroll(Modal): 装的是**同一个**共用基类的方法, 不是另抄一份。
import { withInfiniteScroll } from '../utils/InfiniteScroll';
// v0.19.22 — 后端错误文案的**共用入口** (三级兜底, 与本文件历史手抄版逐字等价)
import { errReason } from '../utils/FormPrimitives';

/* ============================================================================
 * ★★★ 决策 I (v0.17.14, 辉哥 10/13 拍板) — 合并字段的顺序开关 ★★★
 *
 * 合并字段 = 「课程类型 + 课程分类」渲染成一段文字。
 *
 *   类型在前 → 「小班单板」 / 「私教公园」
 *   分类在前 → 「单板小班」 / 「公园私教」   ← 辉哥最初举的例子是这个
 *
 * ⚠️ **注意实际渲染**: 辉哥最初的例子是「单板小班」(分类在前),
 *    本轮跟预约管理列表 (LessonsTab.js) 统一成**类型在前**后,
 *    真实数据 lesson 102 渲染出来是「**小班单板**」而不是「单板小班」。
 *    这是辉哥批准的效果, 但看着别扭就是改这一个常量的事 ——
 *    把它换成 ['category', 'type'] 就翻回分类在前, **别去别处散着改**。
 *
 * 同 LessonsTab.js:361-371 的排列 (类型 .ZS-type-tag 在前、分类 .ZS-category-tag 在后),
 * 保证同一个字段在两个 tab 里读起来是同一顺序。
 *
 * 类型与分类之间**不加任何分隔符** (直接相贴), 分类之间用 ' · '
 * —— 沿用 LessonsTab.js:453 渲染多分类的既有写法, 不是本轮发明的。
 * ========================================================================== */
export const MERGED_COURSE_FIELD_ORDER = ['type', 'category'];

/** 课程类型 id —— 优先 lesson 表 (约课时实际选的), fallback 卡 template (默认配置)
 *  同 LessonsTab.js:216-218 */
export function lessonTypeIdOf(l) {
  if (!l) return null;
  return l.lesson_type_id || l.student_card && l.student_card.template && l.student_card.template.lesson_type_id || null;
}

/** 课程分类 ids 数组 —— lesson 表是单值 string (包成数组), 否则取卡 template 的多选数组
 *  同 LessonsTab.js:224-230 */
export function lessonCategoryIdsOf(l) {
  if (!l) return [];
  if (l.lesson_category_id) return [l.lesson_category_id];
  if (l.student_card && l.student_card.template && Array.isArray(l.student_card.template.lesson_category_ids)) {
    return l.student_card.template.lesson_category_ids;
  }
  return [];
}

/**
 * 合并字段的显示文本 —— 类型名 + 分类名, 都空时返 null (渲染层出灰色 '—',
 * 同 LessonsTab.js:459 的既有惯例)。名字一律走 LessonOptions.labelFor(), 不硬编码中文。
 *
 * @param {object} l lesson 行 (LessonSerializer 的出参形状, 两个端点共用)
 * @returns {string|null}
 */
export function mergedTypeCategoryLabel(l) {
  const typeId = lessonTypeIdOf(l);
  const type = typeId ? LessonOptions.labelFor('type', typeId) : '';
  const cats = lessonCategoryIdsOf(l).map(cid => LessonOptions.labelFor('category', cid)).filter(Boolean);
  const partOf = {
    type: type,
    category: cats.join(' · ') // 多分类分隔符, 沿用 LessonsTab.js:453
  };
  const label = MERGED_COURSE_FIELD_ORDER.map(k => partOf[k]).filter(Boolean).join(''); // 类型与分类之间**不加分隔符**
  return label || null;
}

/**
 * 决策 K (辉哥 10/13 拍板) —— 这个 pill 里的值是不是「卡模板默认配置」兜底来的?
 *
 * 卡模板默认配置 ≠ 约课时实际选的 (LessonsTab.js:356 讲的是同一件事),
 * 所以 pill 上打个小角标, 让用户分得出「当时真选的」和「模板配的」。
 *
 * @returns {{on: boolean, type: boolean, category: boolean}}
 */
export function mergedFieldSourceOf(l) {
  const tpl = !!(l && l.student_card && l.student_card.template);
  return {
    type: !!(l && l.lesson_type_id) === false && tpl && !!lessonTypeIdOf(l),
    category: !!(l && l.lesson_category_id) === false && tpl && lessonCategoryIdsOf(l).length > 0
  };
}

/* ============================================================================
 * 状态徽标 (辉哥 10/13 决策 G: confirmed / checked-in 一起做, 5 个状态全上)
 * 映射文案逐字沿用 LessonsTab.js:102-111 的 statusBadge(), 两个 tab 一套口径。
 *
 * ⚠️ **没有 no_show** —— 决策 H (辉哥 10/13): 这个状态在当前代码里根本不存在,
 *    只活在 migrations/2026_09_23_000006_create_lessons_table.php 的建表 comment
 *    和 tests/pre_v050k_backup.sql 里。写进来会做一个永远不出现的红色「未到」徽标。
 *
 * 颜色沿用设计稿 (紫/蓝/绿/灰 pill 的既有语义色), 取消走灰色 + 整行降饱和:
 *   取消是正常业务结果 (请假 / 场馆活动), 不是异常 —— 同 ZS-td-row.is-cancelled 的处理。
 * ========================================================================== */
const STATUS_BADGES = {
  scheduled: {
    label: '已预约',
    cls: 'gray'
  },
  confirmed: {
    label: '已确认',
    cls: 'blue'
  },
  'checked-in': {
    label: '已签到',
    cls: 'cyan'
  },
  completed: {
    label: '已消课',
    cls: 'green'
  },
  cancelled: {
    label: '已取消',
    cls: 'gray'
  }
};

/* ============================================================================
 * ★ 逐行样式判定 —— v0.17.15 起**只**用于给行加 is-upcoming / is-cancelled 类名
 *
 * ⚠️ **计数口径不再用这三个函数** (v0.17.15 起由后端 meta.counts 提供, 见下方注释)。
 *    保留它们是因为行的染色还要用, 且后端 counts 的 SQL 就是照着这三个判定写的 ——
 *    **两边必须一起改**, 见 StudentLessonHistoryController 的 applyStatusFilter()。
 * ========================================================================== */
export const isCompleted = l => l.status === 'completed';
export const isCancelled = l => l.status === 'cancelled' || Number(l.is_cancelled) === 1;
export const isUpcoming = l => !isCancelled(l) && !isCompleted(l) && new Date(l.start_at) > new Date();

/** 每页条数 —— 辉哥 10/13 拍板: 首屏 20 条, 往下滚动继续 20 条 */
const PAGE_SIZE = 20;

/**
 * sentinel 唯一 id 计数器 —— 用来在 DOM 里认领「哪个 sentinel 是我的」。
 *
 * 为什么不直接 document.querySelector('.ZS-sl-sentinel'): 弹层重建时 (同名学员连开两个)
 * 可能同时存在两个同 class 的 sentinel, 抓错就等于给**别的**弹层加载下一页。
 * 每个实例一个自增 id, 谁加载谁认领, 不串。
 */
let modalSeq = 0;

/**
 * StudentLessonsModal — 「上课详情」弹层 (v0.17.14 建, v0.17.15 改分页滚动加载)
 *
 * 回答一个问题: **这个学员上过哪些课, 每节课上的什么内容, 状态如何。**
 *
 * ## 数据源 (v0.17.15 换端点)
 *   GET /api/ziiven/scheduler/lessons/student-history
 *       ?student_id=<id>&school_id=<id>&limit=20&offset=0[&status=…]
 *   ⚠️ **不能用 GET /lessons**: 那个端点返裸数组且被 CalendarTab / LessonsTab /
 *      日历相关视图三个消费方共用, 改它的响应结构会一次炸三个页面。
 *      分页端点是 v0.17.15 **新增**的第二个端点, 出参 {data, meta} 只服务本弹层。
 *      两边的行结构由后端 LessonSerializer 统一序列化 —— 实测 50 条课逐字节一致。
 *   教室: ListLessons 只返 room_id, 没有联表 room。跟 v0.17.14 一样本弹层单独拉一次
 *      教室列表做 id→name 映射 (教室表很小), 刻意没去给共用端点加 eager load。
 *
 * ## ★ 汇总数字为什么必须读 meta (v0.17.15, 辉哥 10/13 拍板 配套条 1)
 *   改前 4 格汇总 + 4 个 chip 的数字是**前端**基于 this.lessons 全量数组自己算的。
 *   分页之后手上只有 20 条, 算出来的「总课次」会随滚动一路往上跳 (20→40→45)。
 *   ⇒ 数字全部来自后端 meta.counts / meta.total / meta.total_charge, **不再从 this.lessons 算**。
 *
 * ## ★ 筛选为什么走后端 (v0.17.15, 辉哥 10/13 拍板 配套条 2)
 *   点 chip = offset 归零 + 带 status 重新请求。改前是前端 filter 已加载的数组 ——
 *   分页后那样做, 点「已消课」只能在这 20 条里筛, 一条都筛不出来。
 *
 * ## ★ 滚动容器是 .ModalManager (v0.17.15 实测)
 *   IntersectionObserver 的 root **必须**配对, 否则永远不触发或误触发。
 *   本弹层的真实情况 (1440×900 实测, 45 条课):
 *     .ModalManager  overflow-y:auto  scrollH=4795 clientH=900  ← 唯一真正滚动的
 *     .Modal-content overflow:hidden   scrollH=4555 clientH=4555  (不滚)
 *     .Modal-body    overflow:visible  scrollH=4403 clientH=4403  (不滚, 本弹层没吃到 62vh 那条)
 *     window                          scrollY=0, body overflow:hidden
 *   ⇒ 代码里不写死 '.ModalManager', 而是**从 sentinel 往上找第一个 overflow-y:auto/scroll 的祖先**,
 *     这样 Flarum 改类名或换成别的布局时也不会静默失效。
 *
 * ## 实现约束
 *   ⚠️ **绝不能定义 onready()** —— `ziven-core /js/src/forum/index.js` (⚠️ 目录名尾部带空格)
 *      里 `safeOverride(Modal, 'onready', …)` 强制所有 modal 聚焦首个 input, 修 iOS 14+
 *      打开瞬间页面跳顶部。子类一旦定义 onready 就会**遮蔽**那个 patch 直接把修复打掉
 *      (v0.17.13 刚踩过, 见 LessonEditorModal.js:300-306)。本文件用 onremove 做清理, 不碰 onready。
 *   ⚠️ 事件属性一律小写 (onclick / oninput ...) —— Flarum 2 的 mithril 绑定走
 *      `addEventListener(r.slice(2))`, **没有 toLowerCase**, onClick 永不触发且不报错。
 */
export default class StudentLessonsModal extends withInfiniteScroll(Modal) {
  oninit(vnode) {
    super.oninit(vnode);
    this.student = vnode.attrs.student || {};
    this.schoolId = vnode.attrs.schoolId || null;
    this.loading = true;
    this.loadingMore = false;
    this.error = '';
    this.lessons = [];
    this.roomNameById = {};
    this.filter = 'all';
    this.expanded = {}; // lesson id → true (展开区开合状态)
    // ── v0.17.15 分页状态 ──
    this.meta = null; // 后端汇总 (counts / total / total_charge / has_more)
    this.offset = 0; // 已加载条数 (= 下一批的 offset)
    this.reqSeq = 0; // 竞态守卫: 只有最新一次请求的结果算数
    this.paginatedOnce = false; // 是否翻过页 —— 决定要不要显示「已全部加载」
    this._observer = null; // IntersectionObserver 实例
    this._syncRaf = null; // 待执行的 rAF (syncObserver 重试), 关闭时要撤掉
    this._sid = 'sl' + ++modalSeq; // 本实例 sentinel 的 DOM 认领 id
    // 批 41 · 段① — 哨兵 DOM 属性名 (共用基类 utils/InfiniteScroll.js 按它认领 sentinel)
    this.sentinelAttr = 'data-slsid';
    this._roomsPromise = null;
    // ── v0.18.0 (辉哥 10/18 拍板) — 课评三态 ──
    //   ⚠️ 这里是**学员端**: 服务端只返回 published, 草稿压根不会出现在这张表里 (R2)。
    //      所以本组件里**没有**任何「草稿」分支 —— 不是忘了处理, 是服务端不给你看。
    //   ⚠️ 「暂无课评」灰字是辉哥拍板**保留**的 (规则 6): 从未写与「教练有草稿但没发布」
    //      在学员端**逐像素同形** —— 这正是草稿对学员不可见的可见证据。
    this.reviewMap = {};
    this.reviewSeq = 0;
    // LessonOptions 其它 tab 是懒加载的 (CardTemplatesTab / LessonEditorModal 才调 load)。
    // 本弹层要在渲染时把 id 转成中文名, 自己兜一次 load() —— 它内部有 _loaded 缓存, 幂等。
    LessonOptions.load();
    this.loadFirstPage();
  }

  /**
   * 弹层关闭时拆掉 IntersectionObserver。
   *
   * ⚠️ **漏掉这里 = 每次开关弹层泄漏一个 observer**, observer 持有 sentinel 引用,
   *    sentinel 持有已经离屏的 DOM —— 连开 20 次就是 20 个孤儿在跑回调 (验收判据 7)。
   */
  onremove(vnode) {
    this.disconnectObserver();
    // 撤掉排队中的重试帧 —— 否则弹层关了它还会醒来跑一次 syncObserver
    if (this._syncRaf) {
      cancelAnimationFrame(this._syncRaf);
      this._syncRaf = null;
    }
    if (super.onremove) super.onremove(vnode);
  }
  className() {
    // 跟 TrialDetailModal 同款 (Modal--medium 负责 .Modal-body 的深色底 + 文字色)。
    // ⚠️ 兄弟 modal 上那个 `Modal--fullscreen-padded` 在本站 forum.css 里**查无此规则**
    //    (实测: `.Modal--fullscreen-padded` 0 命中, `.Modal--medium` 才有定义),
    //    所以这里不跟着抄一个空类。
    return 'Modal--medium ZS-StudentLessonsModal';
  }

  /**
   * v0.18.82 — 该学员名下**有没有卡** (空态文案要区分「有卡但没课」与「没卡也没课」)
   *
   * ## 数据来源 (⛔ 零新增接口 · ⛔ 零后端返回结构改动)
   *   `StudentsListTab.openStudentLessons(s)` 传进来的 `student` 对象**自带 `cards` 数组** ——
   *   后端 `ListStudentsController` 的两条分支 (有卡 / 没卡) 都把 `cards` 键塞上了
   *   (没卡学员 = `[]`, 不是 undefined) ⇒ **现成的数据, 不用再拉一次**。
   *   ⛔ 本仓 `ListMyCardsController` (`GET /my-cards`) 是**学员本人**的只读列表,
   *      员工端拿它查「别人」⇒ 不能用; 本方法走的是学员列表带过来的 `cards`。
   *
   * ## 为什么值得单独判一次
   *   改前空态只有一句「这位学员还没有上课记录」⇒ 把**数据问题**说成**业务事实**:
   *   学员名下明明有卡 (甚至有课), 弹层却一口咬定「没有上课记录」。
   *
   * @returns {boolean}
   */
  hasCards() {
    const cards = this.student && this.student.cards;
    return Array.isArray(cards) && cards.length > 0;
  }
  title() {
    return m("span", null, m("i", {
      className: "fas fa-graduation-cap",
      style: "color:var(--zs-purple-a78bfa, #a78bfa);margin-right:8px;"
    }), "\u4E0A\u8BFE\u8BE6\u60C5");
  }
  redraw() {
    if (typeof m !== 'undefined' && m && typeof m.redraw === 'function') m.redraw();
  }

  /* ═══════════════════════ 数据加载 ═══════════════════════ */

  /** 教室名只能前端映射 —— 只在首屏拉一次, 翻页不重复拉 (v0.17.14 起沿用) */
  loadRooms() {
    if (!this._roomsPromise) {
      this._roomsPromise = Api.list('rooms', this.schoolId ? {
        school_id: this.schoolId
      } : {}).then(rooms => {
        const map = {};
        (rooms || []).forEach(r => {
          map[r.id] = r.name;
        });
        this.roomNameById = map;
      })
      // 失败不阻断弹层 —— 降级成 '—'
      .catch(() => {
        this.roomNameById = {};
      });
    }
    return this._roomsPromise;
  }

  /** 首屏 / 换筛选: 清空重取 (offset 归零) */
  loadFirstPage() {
    this.lessons = [];
    this.offset = 0;
    this.meta = null;
    this.paginatedOnce = false;
    this.loading = true;
    this.disconnectObserver();
    this.redraw();
    this.fetchPage(0, 'replace');
  }

  /**
   * 拉一页。
   *
   * @param {number}  offset
   * @param {'replace'|'append'}  mode  replace=替换 (首屏/换筛选) / append=追加 (滚动)
   */
  async fetchPage(offset, mode) {
    // 每次请求领一个递增序号; 响应回来时序号不是最新的就**整个丢弃**。
    // 这是防竞态的核心 (验收判据 6): 快速连点筛选 / 快速连续触底时,
    // 旧请求的响应**不能**覆盖新请求的结果, 否则会看到两个筛选的结果混在一起。
    const seq = ++this.reqSeq;
    try {
      const params = {
        student_id: this.student.user_id,
        limit: PAGE_SIZE,
        offset: offset
      };
      if (this.schoolId) params.school_id = this.schoolId;
      // 'all' 是后端的默认值, 不传 —— 少一个 query 参数, 也避免后端被塞非法值
      if (this.filter !== 'all') params.status = this.filter;
      const res = await Api.get('lessons/student-history', params);
      // ★ 过期响应直接扔掉, 一个字段都不碰
      if (seq !== this.reqSeq) return;
      const rows = res && res.data || [];
      this.meta = res && res.meta || null;
      this.lessons = mode === 'replace' ? rows : this.lessons.concat(rows);
      // v0.18.0 — 课评三态跟着这一批一起拉 (追加页里有**新**的课次)
      this.loadReviewStates(rows);
      this.offset = offset + rows.length;
      if (mode === 'append') this.paginatedOnce = true;
      this.error = '';
    } catch (e) {
      if (seq !== this.reqSeq) return;
      console.error('[student-lessons] page load failed', e);
      // v0.17.65 — 为什么不能只看 `e.message` (改前这里只有 `|| '加载失败'`)
      //   后端抛 ValidationException 时 HTTP 是 422, **真实原因在 `e.response.errors[0].detail`**;
      //   `e.message` 只是请求层拼的 "Response code 422 (Unprocessable Entity)", 跟后端那句提示无关。
      //   后果: 学员课次翻页失败时只显示「加载失败」, 看不出是**无权限**还是**学员 id 传错**还是**分页参数非法**,
      //   「上拉加载更多」也一并在空白上失败, 用户只能反复上拉。
      //   形状照抄同仓既有正确写法 (CalendarTab.js:2770 / modal/FixedLessonModal.js:593);
      //   兜底文案「加载失败」保留不变, 只是降为最后一档 (网络层异常 / 非 422 时仍命中)。
      this.error = errReason(e, '加载失败');
      if (mode === 'replace') this.lessons = [];
    } finally {
      // ⚠️ 只有最新那次请求才允许改 loading 标志 —— 被丢弃的旧请求如果也去改,
      //    会把新一轮请求的 loading 提前清掉, 造成重复触发。
      if (seq === this.reqSeq) {
        this.loading = false;
        this.loadingMore = false;
      }
      this.redraw();
      this.loadRooms();
      this.syncObserver();
    }
  }

  /* ═══════════════════════ IntersectionObserver (现在共用 utils/InfiniteScroll.js) ═══════════════════════ */
  //
  //   findSentinel / disconnectObserver / scheduleSync / loadNextPage 四个方法已挪到共用基类
  //   utils/InfiniteScroll.js (批 41 · 段①)。
  //
  //   ⚠️ 下面两个方法 (findScrollRoot / syncObserver) 是**本弹层自己的覆写**:
  //   它们是 **v0.17.15 的旧版实现** (比 tab 那三份旧) ——
  //     · findScrollRoot: 无 body/documentElement 排除、无 scrollHeight 判定
  //     · syncObserver:   无 if (this._destroyed) return; 闸
  //   ⇒ **本批只搬不改** (⛔ 不许"顺手统一" —— 那是**批 43** 的事, 属真 bug 修复)。

  /**
   * 从 sentinel 往上找**真正会滚**的那个祖先。
   *
   * 只认 overflow-y 为 auto/scroll 的元素 —— 'visible' 不滚, 'hidden' 也不滚 (它是裁切不是滚动)。
   * 找不到就返回 null (= 用视口作 root), 宁可退化也不要崩。
   *
   * ⚠️⚠️ **这是 v0.17.15 的旧版实现, 本批只搬不改** (统一它属**批 43**)
   *   与 tab 那三份的差别: **没有**排除 body/documentElement, **也没有** scrollHeight 判定。
   */
  findScrollRoot(el) {
    let node = el && el.parentElement;
    while (node) {
      const ov = window.getComputedStyle(node).overflowY;
      if (ov === 'auto' || ov === 'scroll') return node;
      node = node.parentElement;
    }
    return null;
  }

  /**
   * (重新)挂 observer。
   *
   * 每次渲染之后都要调 —— 因为 sentinel 是随列表 DOM 一起被重建的, 旧 observer
   * observe 的是**已经被替换掉**的那个节点, 不重挂就永远收不到回调。
   *
   * ⚠️⚠️ **为什么必须等下一帧** (v0.17.15 实测踩到, 别"优化"掉)
   *   本仓库的 `m.redraw` 不是原生 mithril redraw —— Flarum 包装过它
   *   (实测: 手动删掉 sentinel 再 m.redraw(), 紧接着同步查 DOM **仍然是旧的**)。
   *   所以「redraw(); syncObserver();」这种同一段代码里紧挨着的写法,
   *   syncObserver 跑的时候 sentinel 还没进 DOM → 提前 return → observer **一次都建不出来**
   *   (调试表现: 全程只有 1 个网络请求, 滚到底纹丝不动)。
   *   ⇒ 找不到 sentinel 时推到下一帧重试, 并且**限次** (最多 5 次),
   *      否则 has_more=true 但 sentinel 迟迟不出现时会变成 60fps 空转。
   *
   * @param {number}  tries  已重试次数 (内部传递, 调用方不用管)
   *
   * ⚠️⚠️ **这是 v0.17.15 的旧版实现, 本批只搬不改** (统一它属**批 43**)
   *   与 tab 那三份的差别: **没有** `if (this._destroyed) return;` 闸 (这正是「切走 tab 后
   *   observer 泄漏」的成因)。
   */
  syncObserver(tries) {
    if (tries === void 0) {
      tries = 0;
    }
    this.disconnectObserver();
    if (this.error) return;
    const meta = this.meta;
    if (!meta || !meta.has_more) return; // has_more=false ⇒ 不装, 自然不再发请求
    if (this.loading || this.loadingMore) return;
    const sentinel = this.findSentinel();
    if (!sentinel) {
      if (tries < 5) this.scheduleSync(tries + 1);
      return;
    }
    this._observer = new IntersectionObserver(entries => {
      // isIntersecting 而不是 entry.isIntersecting 单条 —— 一次回调可能给多条
      if (entries.some(e => e.isIntersecting)) this.loadNextPage();
    }, {
      root: this.findScrollRoot(sentinel),
      // 提前 200px 就开始拉: 等 sentinel 真的滚进视口再加载会明显卡一下
      rootMargin: '0px 0px 200px 0px',
      threshold: 0
    });
    this._observer.observe(sentinel);
  }

  /* ═══════════════════════ 汇总 (只读 meta) ═══════════════════════ */

  counts() {
    const m = this.meta;
    if (!m || !m.counts) return {
      all: 0,
      completed: 0,
      upcoming: 0,
      cancelled: 0
    };
    const c = m.counts;
    return {
      all: Number(c.all) || 0,
      completed: Number(c.completed) || 0,
      upcoming: Number(c.upcoming) || 0,
      cancelled: Number(c.cancelled) || 0
    };
  }
  totalUnits() {
    const m = this.meta;
    // ⚠️ 不再 reduce this.lessons —— 那样算出来的是「已加载的 N 条」的课时,
    //    会随滚动往上跳 (验收判据 4)。
    return m ? Number(m.total_charge) || 0 : 0;
  }
  totalLessons() {
    const m = this.meta;
    return m ? Number(m.total) || 0 : 0;
  }
  setFilter(f) {
    if (this.filter === f) return;
    this.filter = f;
    // 点 chip = 重新请求 (offset 归零), 不是前端过滤已加载的那 20 条
    this.loadFirstPage();
  }
  toggleMore(id) {
    this.expanded[id] = !this.expanded[id];
    this.redraw();
  }

  /** '2026-10-08 09:00:00' + '2026-10-08 10:00:00' → 分钟数 */
  durationMin(l) {
    if (!l.start_at || !l.end_at) return 0;
    const s = new Date(l.start_at);
    const e = new Date(l.end_at);
    const min = Math.round((e.getTime() - s.getTime()) / 60000);
    return min && min > 0 ? min : 0;
  }
  roomName(l) {
    if (!l.room_id) return '—';
    return this.roomNameById[l.room_id] || '—';
  }

  /** 教练色点 —— 用这节课自己的 coach_color (v0.8.10 已按课所属校区取好) */
  coachDot(l) {
    const raw = l.coach && l.coach.coach_color;
    const bg = typeof raw === 'string' && /^#[0-9a-fA-F]{3,8}$/.test(raw) ? raw : 'var(--zs-brand-2d7be5, #2D7BE5)';
    return m("i", {
      className: "ZS-sl-coach-dot",
      style: 'background:' + bg,
      "aria-hidden": "true"
    });
  }
  studentAvatar() {
    const uid = this.student && this.student.user_id;
    // 跟 StudentsListTab.UserAvatarLink / LessonsTab.studentAvatar 同款: 先查 store,
    // 查不到就异步 find 预热 (失败静默)。拿不到 model 时退字母头像 —— 名字就在旁边, 头像是装饰。
    const u = uid ? app.store.getById('users', uid) : null;
    if (!u && uid) app.store.find('users', uid).catch(() => null);
    if (u) return m("span", {
      className: "ZS-sl-avatar"
    }, m(Avatar, {
      user: u
    }));
    const name = this.student && (this.student.nickname || this.student.username) || '学';
    return m("span", {
      className: "ZS-sl-avatar is-letter"
    }, name.charAt(0).toUpperCase());
  }

  /** 课程类型/分类明细 (折叠区) —— 带「来自卡模板默认配置」来源标注 */
  sourceNote(on) {
    return on ? ' · 来自卡模板默认配置' : '';
  }
  renderCoursePill(l) {
    const label = mergedTypeCategoryLabel(l);
    if (!label) {
      return m("span", {
        className: "ZS-sl-course is-empty",
        title: "\u672A\u914D\u7F6E\u8BFE\u7A0B\u5206\u7C7B / \u8BFE\u7A0B\u7C7B\u578B"
      }, "\u2014");
    }
    const src = mergedFieldSourceOf(l);
    const isTpl = src.type || src.category;
    const which = src.type && src.category ? '课程类型与分类' : src.type ? '课程类型' : '课程分类';
    return m("span", {
      className: "ZS-sl-course",
      title: isTpl ? which + ' 来自卡模板默认配置, 不是约课时实际选的' : '课程类型 + 课程分类'
    }, label, isTpl && m("span", {
      className: "ZS-sl-sup",
      title: which + ' 来自卡模板默认配置, 不是约课时实际选的'
    }, "*"));
  }

  /* ═══════════════════════ v0.18.0 课评 (辉哥 10/18 拍板) ═══════════════════════ */

  /**
   * 学员端的课评三态。
   *
   * ⚠️ **没有权限判据**: 读课评走的是既有的 `scheduler.viewLessons` (学员组本来就有),
   *   服务端再按 R2 收窄到「只看自己 + 只看已发布 + 只看已消课」。
   *   前端额外判断一律没有意义 —— 学员端本来就没有写位, 有没有 `canReviewLessons` 都是 false。
   * ⚠️ 失败静默: 拿不到就全当「暂无课评」, 不影响课表本身。
   */
  async loadReviewStates(rows) {
    const ids = (rows || []).map(l => l.id).filter(Boolean);
    if (ids.length === 0) return;
    const seq = ++this.reviewSeq;
    const map = await fetchReviewMap(ids);
    if (seq !== this.reviewSeq) return;
    this.reviewMap = Object.assign({}, this.reviewMap, map);
    this.redraw();
  }
  reviewOf(l) {
    return l && this.reviewMap && this.reviewMap[l.id] || null;
  }

  /**
   * 学员行的课评入口 (规则 20: 点开是**只读** modal, 无任何可编辑控件)。
   *
   * ⛔ 草稿 / 从未写 / 课次已取消 ⇒ 一律渲染**灰色「暂无课评」**文字, 不是入口。
   *   辉哥 10/18 拍板保留这句灰字 —— 它就是「草稿对学员不存在」的可见证据。
   */
  renderReviewEntry(l) {
    const d = this.reviewOf(l);
    if (!d || d.status !== 'published') {
      return m("span", {
        className: "ZS-sl-review is-empty"
      }, "\u6682\u65E0\u8BFE\u8BC4");
    }
    return m("span", {
      className: "ZS-sl-review",
      title: "\u67E5\u770B\u6559\u7EC3\u7684\u8BFE\u8BC4",
      onclick: () => openReviewModal({
        lesson: l,
        descriptor: d,
        readonly: true
      })
    }, m("i", {
      className: "fas fa-star",
      style: "margin-right:4px;"
    }), starsFor(d.rating), " \u8BFE\u8BC4");
  }
  renderRow(l) {
    const cancelled = isCancelled(l);
    const upcoming = isUpcoming(l);
    const open = !!this.expanded[l.id];
    const badge = STATUS_BADGES[l.status] || {
      label: l.status || '—',
      cls: 'gray'
    };
    const dur = this.durationMin(l);
    const card = l.student_card;
    const tplType = lessonTypeIdOf(l);
    const tplCats = lessonCategoryIdsOf(l);
    const src = mergedFieldSourceOf(l);

    // 角标: 状态 + 半节 / 体验 / 固定课 (设计稿 §1.5)
    const tags = [m("span", {
      className: 'ZS-sl-tag ' + badge.cls,
      key: "status"
    }, badge.label)];
    if (Number(l.lesson_charge) === 0.5) tags.push(m("span", {
      className: "ZS-sl-tag cyan",
      key: "half"
    }, "\u534A\u8282"));
    if (l.is_trial) tags.push(m("span", {
      className: "ZS-sl-tag amber",
      key: "trial"
    }, "\u4F53\u9A8C"));
    if (l.fixed_lesson_id) tags.push(m("span", {
      className: "ZS-sl-tag blue",
      key: "fixed"
    }, "\u56FA\u5B9A\u8BFE"));
    const extra = [];
    extra.push(['课程类型', (tplType ? LessonOptions.labelFor('type', tplType) : '—') + this.sourceNote(src.type)]);
    extra.push(['课程分类', (tplCats.length ? tplCats.map(c => LessonOptions.labelFor('category', c)).join('、') : '—') + this.sourceNote(src.category)]);
    extra.push(['计费课时', Number(l.lesson_charge) === 0.5 ? '0.5 课时（半节）' : (Number(l.lesson_charge) || 1).toFixed(1) + ' 课时']);
    extra.push(['使用卡', card ? (card.template && card.template.name || card.name || '卡片') + ' · 余 ' + card.remaining_lessons + '/' + card.total_lessons + ' · 有效期至 ' + (card.expires_at || '不限') : '未绑定学员卡']);
    if (l.fixed_lesson_id) {
      const f = l.fixed;
      extra.push(['固定课溯源', '#' + l.fixed_lesson_id + (f ? ' · 每周' + f.day_label + ' ' + String(f.start_time || '').slice(0, 5) : '')]);
    }
    if (l.sales_followup_id) extra.push(['体验溯源', '跟进单 #' + l.sales_followup_id]);
    return m("div", {
      className: 'ZS-sl-row' + (upcoming ? ' is-upcoming' : '') + (cancelled ? ' is-cancelled' : ''),
      key: l.id
    }, m("div", {
      className: "ZS-sl-rowhead"
    }, m("div", {
      className: "ZS-sl-time"
    }, m("span", {
      className: "d"
    }, String(l.start_at || '').slice(0, 10)), ' ' + String(l.start_at || '').slice(11, 16) + '–' + String(l.end_at || '').slice(11, 16), dur > 0 && m("span", {
      className: "ZS-sl-dur"
    }, dur, " \u5206\u949F")), this.renderCoursePill(l), m("div", {
      className: "ZS-sl-tags"
    }, tags)), m("div", {
      className: "ZS-sl-fields"
    }, m("div", {
      className: "ZS-sl-f"
    }, m("em", null, "\u6559\u7EC3"), m("span", null, this.coachDot(l), l.coach ? l.coach.display_name || l.coach.username : '—')), m("div", {
      className: "ZS-sl-f"
    }, m("em", null, "\u6559\u5BA4"), m("span", null, this.roomName(l))), m("div", {
      className: "ZS-sl-f"
    }, m("em", null, "\u8BFE\u8BC4"), m("span", null, this.renderReviewEntry(l)))), m("button", {
      className: "ZS-sl-more-btn",
      onclick: () => this.toggleMore(l.id)
    }, m("i", {
      className: 'fas fa-chevron-down' + (open ? ' is-open' : ''),
      "aria-hidden": "true"
    }), m("span", null, open ? '收起' : '展开更多信息')), open && m("div", {
      className: "ZS-sl-more"
    }, m("div", {
      className: "ZS-sl-fields"
    }, extra.map(kv => m("div", {
      className: "ZS-sl-f",
      key: kv[0]
    }, m("em", null, kv[0]), m("span", null, kv[1])))), l.notes ? m("div", {
      className: "ZS-sl-note"
    }, m("b", null, "\u5907\u6CE8"), "\u3000", l.notes) : null));
  }
  content() {
    const s = this.student;
    const c = this.counts();
    const name = s.nickname || s.username || '—';
    const total = this.totalLessons();
    const hasRows = total > 0;
    let body;
    if (this.loading) {
      body = m("div", {
        style: "padding:30px;text-align:center;color:var(--zs-text-2-9ca3af, #9ca3af);"
      }, "\u52A0\u8F7D\u4E2D...");
    } else if (this.error) {
      body = m("div", {
        className: "ZS-sl-note",
        style: "color:var(--zs-danger-ff8a80, #ff8a80);"
      }, this.error);
    } else if (!hasRows) {
      // ── v0.18.82 — 空态文案区分两种情况 ────────────────────────────────────
      //   改前只有一句「这位学员还没有上课记录」 ⇒ 把**数据问题**伪装成了**业务事实**:
      //   学员明明名下有卡 (甚至有课), 弹层却一口咬定「没有上课记录」。
      //   现在按 `hasCards()` 分两种:
      //     A 有卡但 0 节课 ⇒ 说清楚是「有卡但没有**可归属**的上课记录」(⛔ 不许说「没有上课记录」);
      //     B 无卡 0 节课   ⇒ 保留原文案一字不改。
      body = m("div", {
        className: "ZS-sl-empty"
      }, m("div", {
        className: "ico"
      }, m("i", {
        className: "fas fa-calendar-days",
        "aria-hidden": "true"
      })), this.hasCards() ? m('[', null, m("div", {
        className: "t"
      }, "\u8FD9\u4F4D\u5B66\u5458\u540D\u4E0B\u6709\u5361\uFF0C\u4F46\u8FD8\u6CA1\u6709\u53EF\u5F52\u5C5E\u7684\u4E0A\u8BFE\u8BB0\u5F55"), m("div", {
        className: "d"
      }, "\u5171 0 \u8282\u8BFE \xB7 \u7D2F\u8BA1 0 \u8BFE\u65F6", m("br", null), "\u540D\u4E0B\u5361\u7247\u9700\u6709\u5BF9\u5E94\u7684\u7EA6\u8BFE\u8BB0\u5F55\u624D\u4F1A\u51FA\u73B0\u5728\u8FD9\u91CC")) : m('[', null, m("div", {
        className: "t"
      }, "\u8FD9\u4F4D\u5B66\u5458\u8FD8\u6CA1\u6709\u4E0A\u8BFE\u8BB0\u5F55"), m("div", {
        className: "d"
      }, "\u5171 0 \u8282\u8BFE \xB7 \u7D2F\u8BA1 0 \u8BFE\u65F6", m("br", null), "\u7EA6\u8BFE\u540E\u4F1A\u5728\u8FD9\u91CC\u6309\u300C\u4ECE\u65B0\u5230\u65E7\u300D\u81EA\u52A8\u7D2F\u79EF")));
    } else if (this.lessons.length === 0) {
      body = m("div", {
        className: "ZS-sl-list"
      }, m("div", {
        style: "padding:24px;text-align:center;color:var(--zs-text-2-9ca3af, #9ca3af);"
      }, "\u8BE5\u7B5B\u9009\u4E0B\u6CA1\u6709\u8BFE\u7A0B\uFF08\u5171 ", total, " \u8282\u8BFE\uFF09"));
    } else {
      const meta = this.meta;
      const showEnd = meta && !meta.has_more;
      body = m("div", {
        className: "ZS-sl-list"
      }, this.lessons.map(l => this.renderRow(l)), meta && meta.has_more && m("div", {
        className: "ZS-sl-sentinel",
        "data-slsid": this._sid,
        "aria-hidden": "true"
      }), this.loadingMore && m("div", {
        className: "ZS-sl-loading"
      }, m("i", {
        className: "fas fa-spinner fa-spin",
        "aria-hidden": "true"
      }), " \u6B63\u5728\u52A0\u8F7D\u2026"), showEnd && this.paginatedOnce && m("div", {
        className: "ZS-sl-end"
      }, "\u5DF2\u5168\u90E8\u52A0\u8F7D\uFF08\u5171 ", total, " \u8282\u8BFE\uFF09"));
    }
    return m("div", null, m("div", {
      className: "Modal-body"
    }, m("div", {
      className: "ZS-sl-head"
    }, m("div", {
      className: "ZS-sl-who"
    }, this.studentAvatar(), m("span", {
      className: "nm"
    }, name), m("span", {
      className: "un"
    }, "@", s.username || ''), m("span", {
      className: "ZS-sl-count"
    }, total, " \u8282\u8BFE")), m("div", {
      className: "ZS-sl-stats"
    }, m("div", {
      className: 'ZS-sl-stat' + (c.all ? '' : ' zero')
    }, m("div", {
      className: "v"
    }, c.all), m("div", {
      className: "k"
    }, "\u603B\u8BFE\u6B21")), m("div", {
      className: 'ZS-sl-stat blue' + (c.completed ? '' : ' zero')
    }, m("div", {
      className: "v"
    }, c.completed), m("div", {
      className: "k"
    }, "\u5DF2\u6D88\u8BFE")), m("div", {
      className: 'ZS-sl-stat green' + (c.upcoming ? '' : ' zero')
    }, m("div", {
      className: "v"
    }, c.upcoming), m("div", {
      className: "k"
    }, "\u5F85\u4E0A")), m("div", {
      className: 'ZS-sl-stat' + (this.totalUnits() ? '' : ' zero')
    }, m("div", {
      className: "v"
    }, this.totalUnits()), m("div", {
      className: "k"
    }, "\u7D2F\u8BA1\u8BFE\u65F6")))), hasRows && m("div", {
      className: "ZS-sl-filters"
    }, [['all', '全部', c.all], ['completed', '已消课', c.completed], ['upcoming', '待上', c.upcoming], ['cancelled', '已取消', c.cancelled]].map(chip => m("button", {
      key: chip[0],
      className: 'ZS-sl-chip' + (this.filter === chip[0] ? ' on' : ''),
      onclick: () => this.setFilter(chip[0])
    }, chip[1], " ", m("span", {
      className: "n"
    }, chip[2])))), body, hasRows && m("div", {
      className: "ZS-sl-foot"
    }, "\u5171 ", total, " \u8282\u8BFE\uFF0C\u6309\u4E0A\u8BFE\u65F6\u95F4", m("b", null, "\u4ECE\u65B0\u5230\u65E7"), "\u6392\u5217", this.lessons.length < total ? m("span", null, "\uFF08\u5DF2\u52A0\u8F7D ", this.lessons.length, " \u8282\uFF0C\u7EE7\u7EED\u4E0B\u6ED1\u52A0\u8F7D\u66F4\u591A\uFF09") : null)), m("div", {
      className: "Modal-footer"
    }, m("button", {
      className: "Button Button--primary",
      onclick: () => this.hide()
    }, "\u5173\u95ED")));
  }
}
flarum.reg.add('ziiven-ziven-scheduler', 'forum/modal/StudentLessonsModal', StudentLessonsModal);